import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Paper,
  Chip,
} from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import CodeIcon from '@mui/icons-material/Code';

const apiUrl = process.env.REACT_APP_SECUREIFY_API_URL;
const apiKey = process.env.REACT_APP_SECUREIFY_API_KEY;

function LandingPage() {
  const [deviceId, setDeviceId] = useState(null);
  const [parameters, setParameters] = useState(null);

  useEffect(() => {
    // Initialize SDK and get device fingerprint
    const fp = new window.FingerprintSDK(apiKey, {
      endpoint: apiUrl,
      debug: true
    });

    const getFingerprint = async () => {
      try {
        const id = await fp.generateDeviceId();
        setDeviceId(id);
        const params = await fp.getParameters();
        setParameters(params);
      } catch (error) {
        console.error('Error getting fingerprint:', error);
      }
    };

    getFingerprint();
  }, []);

  return (
    <Box sx={{ flexGrow: 1, pt: 8 }}>
      {/* Hero Section */}
      <Paper
        sx={{
          backgroundColor: 'background.paper',
          pt: 8,
          pb: 6,
          borderRadius: 0,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                component="h1"
                variant="h2"
                color="text.primary"
                gutterBottom
              >
                Secureify: Advanced Device Fingerprinting
              </Typography>
              <Typography variant="h5" color="text.secondary" paragraph>
                Identify and track devices across your web applications with our
                powerful fingerprinting SDK. No cookies required.
              </Typography>
              <Box sx={{ mt: 4 }}>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{ mr: 2 }}
                  startIcon={<CodeIcon />}
                  href="https://docs.secureify.xyz/getting-started"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get Started
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: 'background.default',
                }}
              >
                <CardContent>
                  <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <FingerprintIcon sx={{ mr: 1 }} /> Live Device Fingerprint Demo
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Your Unique Device ID:
                    </Typography>
                    <Chip
                      icon={<FingerprintIcon />}
                      label={deviceId || 'Generating...'}
                      color="primary"
                      sx={{ mb: 1, fontSize: '1.1rem', height: 40 }}
                    />
                  </Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Device Parameters Detected:
                  </Typography>
                  <Box
                    sx={{
                      mt: 2,
                      maxHeight: 400,
                      overflow: 'auto',
                      backgroundColor: 'background.paper',
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#888',
                        borderRadius: '4px',
                      },
                    }}
                  >
                    <pre style={{ margin: 0, fontSize: '0.9rem', lineHeight: 1.5 }}>
                      {parameters
                        ? JSON.stringify(parameters, null, 2)
                        : 'Loading device parameters...'}
                    </pre>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Paper>

      {/* Features Section */}
      <Container sx={{ py: 8 }} maxWidth="lg" id="features">
        <Typography
          component="h2"
          variant="h3"
          align="center"
          color="text.primary"
          gutterBottom
        >
          Features
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <CardContent>
                <FingerprintIcon
                  sx={{ fontSize: 48, mb: 2, color: 'primary.main' }}
                />
                <Typography gutterBottom variant="h5" component="h3">
                  Advanced Fingerprinting
                </Typography>
                <Typography color="text.secondary">
                  Generate unique device identifiers using 50+ parameters including browser attributes, 
                  hardware specifications, and network characteristics.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <CardContent>
                <SecurityIcon
                  sx={{ fontSize: 48, mb: 2, color: 'primary.main' }}
                />
                <Typography gutterBottom variant="h5" component="h3">
                  Enterprise Security
                </Typography>
                <Typography color="text.secondary">
                  Protect against account takeover, prevent fraud, and detect suspicious activities 
                  with our enterprise-grade security solution.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <CardContent>
                <SpeedIcon sx={{ fontSize: 48, mb: 2, color: 'primary.main' }} />
                <Typography gutterBottom variant="h5" component="h3">
                  High Performance
                </Typography>
                <Typography color="text.secondary">
                  Lightweight SDK with minimal impact on page load times and user experience.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default LandingPage;
